import { useEffect, useRef, useState } from "react";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { auth } from "../firebase";
import { useTranslation } from "../i18n/LanguageContext";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

// Reusable admin-facing cover image field: Firebase Storage upload with live
// progress, local preview, replace, remove, and type/size validation. Owns
// its own Storage lifecycle (uploads new files, best-effort deletes the file
// it replaces/removes) so callers only need to persist {url, path} to
// Firestore — see Events.js for usage.
function ImageUploadField({
  label,
  value = "",
  currentPath = "",
  storagePathPrefix,
  onChange,
  disabled = false,
  maxSizeMB = 5,
  helperText = "",
  shape = "rect",
}) {
  const { t } = useTranslation();
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [previewBroken, setPreviewBroken] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const displaySrc = localPreview || value;

  useEffect(() => {
    setPreviewBroken(false);
  }, [displaySrc]);

  const resetInput = () => {
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = (file) => {
    setError("");
    if (!file) return;

    if (!auth.currentUser) {
      setError(t("imageUpload.authRequired", "You must be signed in to upload an image."));
      resetInput();
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t("imageUpload.invalidType", "Unsupported file type. Use JPG, PNG, WEBP, or GIF."));
      resetInput();
      return;
    }
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`${t("imageUpload.tooLarge", "Image is too large. Max size is")} ${maxSizeMB}MB.`);
      resetInput();
      return;
    }

    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(file));
    setUploading(true);
    setProgress(0);

    const storage = getStorage();
    const path = `${storagePathPrefix}/${auth.currentUser.uid}-${Date.now()}-${file.name}`;
    const task = uploadBytesResumable(ref(storage, path), file);

    task.on(
      "state_changed",
      (snapshot) => {
        setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
      },
      () => {
        setUploading(false);
        setError(t("imageUpload.uploadFailed", "Upload failed. Please check your connection and try again."));
        resetInput();
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          // Best-effort cleanup of the file being replaced — never block the
          // user on this, an orphaned old file is harmless.
          if (currentPath && currentPath !== path) {
            deleteObject(ref(storage, currentPath)).catch(() => {});
          }
          onChange?.({ url, path });
        } catch {
          setError(
            t(
              "imageUpload.urlFailed",
              "Upload finished but the image URL could not be retrieved. Please try again."
            )
          );
        } finally {
          setUploading(false);
          resetInput();
        }
      }
    );
  };

  const handleRemove = () => {
    setError("");
    if (currentPath) {
      deleteObject(ref(getStorage(), currentPath)).catch(() => {});
    }
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview("");
    onChange?.({ url: "", path: "" });
    resetInput();
  };

  const isCircle = shape === "circle";
  const resolvedLabel = label ?? t("imageUpload.coverImage", "Cover image");

  const progressBarClasses = isCircle
    ? "absolute inset-x-0 bottom-0 rounded-b-full bg-slate-950/80 px-3 py-1"
    : "absolute inset-x-0 bottom-0 rounded-b-lg bg-slate-950/80 px-2 py-1";

  const preview =
    displaySrc && !previewBroken ? (
      <div className={isCircle ? "relative mx-auto h-32 w-32" : "relative"}>
        <img
          src={displaySrc}
          alt="Preview"
          loading="lazy"
          className={
            isCircle
              ? "h-32 w-32 rounded-full border border-slate-700 object-cover"
              : "h-32 w-full rounded-lg border border-slate-700 object-cover"
          }
          onError={() => setPreviewBroken(true)}
        />
        {uploading && (
          <div className={progressBarClasses}>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700">
              <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="mt-1 text-[10px] text-slate-300">{t("imageUpload.uploading", "Uploading…")} {progress}%</p>
          </div>
        )}
      </div>
    ) : (
      <div
        className={
          isCircle
            ? "mx-auto flex h-32 w-32 items-center justify-center rounded-full border border-dashed border-slate-600 text-center text-xs text-slate-400"
            : "flex h-32 w-full items-center justify-center rounded-lg border border-dashed border-slate-600 text-center text-xs text-slate-400"
        }
      >
        {displaySrc && previewBroken
          ? t("imageUpload.previewBroken", "Image could not be loaded. Choose a new one below.")
          : isCircle
          ? "😊"
          : t("imageUpload.noCoverImage", "No cover image yet")}
      </div>
    );

  return (
    <div className={isCircle ? "space-y-3 text-center" : "space-y-2 rounded-xl border border-slate-700 bg-slate-900/40 p-3"}>
      {resolvedLabel && <span className="block text-xs font-medium text-slate-300">{resolvedLabel}</span>}

      {preview}

      <div className={isCircle ? "flex flex-col items-center gap-2" : "flex flex-wrap items-center gap-3"}>
        <input
          ref={inputRef}
          id={isCircle ? "avatar-file-input" : undefined}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          disabled={disabled || uploading}
          onChange={(e) => handleFile(e.target.files?.[0] || null)}
          className={
            isCircle
              ? "hidden"
              : "flex-1 text-gray-300 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-blue-500 disabled:opacity-50"
          }
        />
        {isCircle && (
          <label
            htmlFor="avatar-file-input"
            className={`inline-flex cursor-pointer items-center justify-center rounded-full border border-slate-600 bg-slate-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-600 ${
              disabled || uploading ? "pointer-events-none opacity-50" : ""
            }`}
          >
            {t("imageUpload.choosePhoto", "Choose photo")}
          </label>
        )}
        {displaySrc && !uploading && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className="text-xs font-semibold text-red-400 transition hover:text-red-300 disabled:opacity-50"
          >
            {t("imageUpload.removeImage", "Remove image")}
          </button>
        )}
      </div>

      {error && <p className="text-[11px] text-red-400">{error}</p>}
      {helperText && !error && <p className="text-[11px] text-amber-300/80">{helperText}</p>}
    </div>
  );
}

export default ImageUploadField;
